/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAtlasTheme } from '@issuemanager/apps/client/theme';
import { Box, Stack, Tooltip, useEventCallback } from '@mui/material';
import { ExtendedTypography } from '../../../extended-typography';
import { useDataGridContext } from '../../data-grid-context';
import type { ExtendedGridColumnHeaderProps } from '../../types';
import { GridSortableHeader } from './grid-sortable-header';

export function GridHeader(params: ExtendedGridColumnHeaderProps) {
  const { colDef, field } = params;
  const { headerName, renderHeaderFilter } = colDef;

  const gridContext = useDataGridContext();
  const theme = useAtlasTheme();

  const handleClick = useEventCallback(() => {
    if (colDef.sortable === false) return;

    const existingSort = gridContext.sortModel?.find(
      (sort) => sort.field === field,
    );
    if (!existingSort) {
      gridContext.setSortModel([{ field, sort: 'asc' }]);
      return;
    }

    if (existingSort.sort === 'asc') {
      gridContext.setSortModel([{ field, sort: 'desc' }]);
      return;
    }

    if (existingSort.sort === 'desc') {
      gridContext.setSortModel([]);
    }
  });

  const sortData = gridContext.sortModel.find(
    (sort) => sort.field === params.field,
  );
  const isAscending = sortData?.sort === 'asc';
  const isDescending = sortData?.sort === 'desc';

  return (
    <Stack
      spacing={1.5}
      onClick={handleClick}
      height={1}
      width={1}
      padding={1.5}
      style={{ cursor: 'pointer' }}
    >
      <Stack direction='row' spacing={1}>
        <Tooltip
          title={
            <ExtendedTypography variant='textSm'>
              {headerName}
            </ExtendedTypography>
          }
          {...theme.presets.TooltipPresets.type.dark}
        >
          <Box
            overflow='hidden'
            textOverflow='ellipsis'
            width={colDef.computedWidth}
          >
            <ExtendedTypography variant='textSm' fontWeight={600} noWrap>
              {headerName}
            </ExtendedTypography>
          </Box>
        </Tooltip>

        {colDef.sortable ? (
          <GridSortableHeader
            isAscending={isAscending}
            isDescending={isDescending}
          />
        ) : null}
      </Stack>

      <Box
        data-testid={`${
          headerName
            ? headerName.toLowerCase().replace(/ /g, '-').concat('-filter')
            : ''
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {renderHeaderFilter?.(params)}
      </Box>
    </Stack>
  );
}
